'use client';

import Link from 'next/link';
import { Suspense, useState, useEffect } from 'react';
import { useSettings, useWishlist } from '@/themes/core/hooks';
import Search from '../navbar/Search';
import CartModal from '../cart/CartModal';
import { Heart, User, Menu as MenuIcon, Search as SearchIcon, ChevronDown } from 'lucide-react';
import { AnnouncementBar } from './AnnouncementBar';
import { Button } from '@/themes/core/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/themes/core/ui/sheet';
import { Menu } from '@/types/navigation';
import { getStoreMenusByIds } from '@/actions/navigation';
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuList,
} from './Navigation';
import { CurrencySelector } from './CurrencySelector';

// Modern Header Props
interface StoreNavigation01Props {
    logo?: string | null;
    siteName?: string;
    menu?: { title: string; path: string }[];

    // Config
    showAnnouncement?: boolean;
    announcementText?: string;
    stickyNavbar?: boolean;
    showSearch?: boolean;
    searchPlaceholder?: string;
    showCart?: boolean;
    showWishlist?: boolean;
    enableCustomerAccounts?: boolean;
    navbar_direction?: "auto" | "rtl" | "ltr";
    customMenus?: Menu[];
    basePath?: string;
    showCloseButton?: boolean;
    announcement_direction?: "auto" | "rtl" | "ltr";
    announcement_icon?: string;
    announcement_link_text?: string;
    announcement_link?: string;
    style?: string;
    banner03_text?: string;
    banner03_link?: string;
    banner03_icon?: string;
    banner03_direction?: "auto" | "rtl" | "ltr";
    stickyAnnouncement?: boolean;
}

export const StoreNavigation01Schema = [
    {
        key: "showAnnouncement",
        label: "عرض شريط الإعلان",
        type: "boolean"
    },
    {
        key: "announcementText",
        label: "نص الإعلان",
        type: "text"
    },
    {
        key: "stickyAnnouncement",
        label: "تثبيت شريط الإعلان",
        type: "boolean"
    },
    {
        key: "showCloseButton",
        label: "إظهار زر الإغلاق",
        type: "boolean"
    },
    {
        key: "stickyNavbar",
        label: "تثبيت القائمة",
        type: "boolean"
    },
    {
        key: "showSearch",
        label: "عرض أيقونة البحث",
        type: "boolean"
    }
];

export const StoreNavigation01DefaultProps = {
    showAnnouncement: true,
    announcementText: "✨ خصم 20% على تشكيلة الصيف | استخدم كود: SUMEMPTY_REPLACEMENT_TO_TRIGGER_VIEW20",
    stickyNavbar: true,
    showSearch: true,
    _fixed: true
};

export function StoreNavigation01(props: StoreNavigation01Props) {
    const settings = useSettings() as any;
    const { items: wishlistItems, openWishlist } = useWishlist();
    const wishlistCount = wishlistItems.length;

    const siteName = props.siteName || settings?.siteName || "Store";
    const logo = props.logo || settings?.logo;
    const menu = props.menu || [];

    const showAnnouncement = props.showAnnouncement ?? true;
    const announcementText = props.announcementText || "Example Announcement";
    const stickyNavbar = props.stickyNavbar ?? false;
    const navbar_direction = props.navbar_direction || "auto";

    // Server Injected Menus
    const injectedMenus = props.customMenus || [];

    // Data Fetching for Menus
    const [customMenus, setCustomMenus] = useState<Menu[]>([]);

    useEffect(() => {
        const fetchMenus = async () => {
            const headerConfig = (settings.header as any) || {};
            let ids: string[] = [];

            if (Array.isArray(headerConfig.menus)) {
                ids = headerConfig.menus;
            } else {
                ids = [
                    headerConfig.menu_1_id,
                    headerConfig.menu_2_id,
                    headerConfig.menu_3_id,
                    headerConfig.menu_4_id
                ].filter(Boolean);
            }

            // 1. If we have server-injected menus, filter them to only those that should be in the header
            if (injectedMenus.length > 0) {
                const filtered = injectedMenus.filter(m => ids.includes(m.id) || ids.includes(m.handle));
                setCustomMenus(filtered);
                return;
            }

            // 2. Otherwise fetch them
            if (ids.length > 0) {
                try {
                    const fetched = await getStoreMenusByIds(ids);
                    // Sort fetched menus based on the order of IDs in the settings
                    const sorted = ids
                        .map(id => fetched.find(m => m.id === id || m.handle === id))
                        .filter((m): m is Menu => !!m);

                    setCustomMenus(sorted);
                } catch (e) {
                    if (process.env.NODE_ENV !== 'production') {
                        console.warn("Failed to fetch menus", e);
                    }
                }
            } else {
                setCustomMenus([]);
            }
        };

        fetchMenus();
    }, [settings.header, injectedMenus]);

    // Resolve Menus
    // Simple Tree Builder
    function buildMenuTree(items: any[]): any[] {
        const map = new Map<string, any>();
        const roots: any[] = [];

        // 1. Create nodes
        items.forEach(item => {
            map.set(item.id, {
                ...item,
                title: item.title,
                path: item.url || '#',
                children: [] // Initialize children
            });
        });

        // 2. Connect tree
        items.forEach(item => {
            const node = map.get(item.id)!;
            const parentId = item.parent_id || item.parentId;

            if (parentId && map.has(parentId)) {
                map.get(parentId)!.children!.push(node);
            } else {
                roots.push(node);
            }
        });

        // 3. Sort by position
        return roots.map(root => {
            if (root.children.length > 0) {
                root.children.sort((a: any, b: any) => (a.position || 0) - (b.position || 0));
            }
            return root;
        }).sort((a, b) => (a.position || 0) - (b.position || 0));
    }

    let displayItems: any[] = [];

    // Combine items from ALL selected menus
    if (customMenus.length > 0) {
        let allItems: any[] = [];
        customMenus.forEach(menu => {
            if (menu.items) {
                allItems = [...allItems, ...menu.items];
            }
        });
        const uniqueItems = Array.from(new Map(allItems.map(item => [item.id, item])).values());
        displayItems = buildMenuTree(uniqueItems);
    } else {
        displayItems = menu;
    }

    // Default menu
    const navMenu = displayItems;

    const resolvePath = (path: string) => {
        if (!path) return '#';
        if (path.startsWith('http')) return path;

        const cleanPath = path.startsWith('/') ? path : `/${path}`;

        // Use passed basePath (e.g., /store/[slug])
        if (props.basePath) {
            // Avoid double slashes if basePath ends with /
            const normalizedBasePath = props.basePath.endsWith('/') ? props.basePath.slice(0, -1) : props.basePath;
            if (!cleanPath.startsWith(normalizedBasePath)) {
                return `${normalizedBasePath}${cleanPath}`;
            }
        }
        return cleanPath;
    };

    // Hover State for Dropdowns
    const [activeMenu, setActiveMenu] = useState<string | null>(null);
    let timeoutId: NodeJS.Timeout;

    const handleMouseEnter = (title: string) => {
        if (timeoutId) clearTimeout(timeoutId);
        setActiveMenu(title);
    };

    const handleMouseLeave = () => {
        timeoutId = setTimeout(() => {
            setActiveMenu(null);
        }, 100);
    };

    return (
        <div className="flex flex-col w-full z-50 relative">
            {/* Announcement - Top */}
            <AnnouncementBar
                showAnnouncement={showAnnouncement}
                announcementText={announcementText}
                stickyAnnouncement={props.stickyAnnouncement}
                showCloseButton={props.showCloseButton}
                announcement_direction={props.announcement_direction}
                announcement_icon={props.announcement_icon}
                announcement_link_text={props.announcement_link_text}
                announcement_link={props.announcement_link}
                style={props.style}
                banner03_text={props.banner03_text}
                banner03_link={props.banner03_link}
                banner03_icon={props.banner03_icon}
                banner03_direction={props.banner03_direction}
            />

            {/* Navbar */}
            <header
                dir={navbar_direction === 'auto' ? undefined : navbar_direction}
                className={`w-full bg-background border-b border-border transition-all ${stickyNavbar ? 'sticky top-0 z-40 shadow-sm' : ''}`}
            >
                <div className="container mx-auto px-4 h-16 flex items-center justify-between">

                    {/* Left: Mobile Menu Trigger & Search Trigger (Mobile) */}
                    <div className="flex items-center gap-2 md:hidden">
                        <Sheet>
                            <SheetTrigger asChild>
                                <Button variant="ghost" size="icon" className="md:hidden" aria-label="فتح القائمة">
                                    <MenuIcon className="h-5 w-5" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent
                                side={navbar_direction === 'rtl' ? "right" : "left"}
                                className={`w-[300px] sm:w-[400px] p-0 ${navbar_direction === 'rtl' ? '[&>button]:left-4 [&>button]:right-auto' : ''}`}
                            >
                                <div
                                    dir={navbar_direction === 'auto' ? undefined : (navbar_direction as any)}
                                    className="flex flex-col h-full bg-background"
                                >
                                    <SheetTitle className="sr-only">القائمة</SheetTitle>
                                    <div className="p-4 border-b flex items-center justify-between">
                                        <span className="text-lg font-bold">{siteName}</span>
                                    </div>
                                    {(props.showSearch ?? true) && (
                                        <div className="p-4 border-b">
                                            <Suspense fallback={<div className="h-10 w-full bg-muted animate-pulse rounded-md" />}>
                                                <Search
                                                    direction={navbar_direction}
                                                    placeholder={props.searchPlaceholder}
                                                />
                                            </Suspense>
                                        </div>
                                    )}
                                    <nav className="flex flex-col py-4">
                                        {navMenu.map((item) => (
                                            <div key={item.title} className="flex flex-col">
                                                <Link
                                                    href={resolvePath(item.path)}
                                                    className="px-6 py-4 text-base font-medium hover:bg-accent transition-colors flex items-center justify-between group"
                                                >
                                                    {item.title}
                                                </Link>
                                                {item.children && item.children.length > 0 && (
                                                    <div className="flex flex-col bg-accent/30 py-1">
                                                        {item.children.map((child: any) => (
                                                            <Link
                                                                key={child.title}
                                                                href={resolvePath(child.path || child.url)}
                                                                className="px-10 py-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                                            >
                                                                {child.title}
                                                            </Link>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </nav>
                                </div>
                            </SheetContent>
                        </Sheet>
                        {(props.showSearch ?? true) && (
                            <Sheet>
                                <SheetTrigger asChild>
                                    <Button variant="ghost" size="icon" className="md:hidden" aria-label="بحث">
                                        <SearchIcon className="h-5 w-5" />
                                    </Button>
                                </SheetTrigger>
                                <SheetContent
                                    side={navbar_direction === 'rtl' ? "right" : "left"}
                                    className={`w-[300px] sm:w-[400px] p-0 ${navbar_direction === 'rtl' ? '[&>button]:left-4 [&>button]:right-auto' : ''}`}
                                >
                                    <div
                                        dir={navbar_direction === 'auto' ? undefined : (navbar_direction as any)}
                                        className="flex flex-col h-full bg-background"
                                    >
                                        <SheetTitle className="sr-only">البحث</SheetTitle>
                                        <div className="p-4 border-b flex items-center justify-between">
                                            <span className="text-lg font-bold">{siteName}</span>
                                        </div>
                                        <div className="p-4 border-b">
                                            <Suspense fallback={<div className="h-10 w-full bg-muted animate-pulse rounded-md" />}>
                                                <Search
                                                    direction={navbar_direction}
                                                    placeholder={props.searchPlaceholder}
                                                />
                                            </Suspense>
                                        </div>
                                        <nav className="flex flex-col py-4">
                                            {navMenu.map((item) => (
                                                <div key={item.title} className="flex flex-col">
                                                    <Link
                                                        href={resolvePath(item.path)}
                                                        className="px-6 py-4 text-base font-medium hover:bg-accent transition-colors flex items-center justify-between group"
                                                    >
                                                        {item.title}
                                                    </Link>
                                                    {item.children && item.children.length > 0 && (
                                                        <div className="flex flex-col bg-accent/30 py-1">
                                                            {item.children.map((child: any) => (
                                                                <Link
                                                                    key={child.title}
                                                                    href={resolvePath(child.path || child.url)}
                                                                    className="px-10 py-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                                                >
                                                                    {child.title}
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </nav>
                                    </div>
                                </SheetContent>
                            </Sheet>
                        )}
                    </div>

                    {/* Logo (Centered on Mobile, Left on Desktop) */}
                    <div className="flex-1 md:flex-none flex justify-center md:justify-start">
                        <Link href="/" className="flex items-center gap-2">
                            {logo ? (
                                <img src={logo} alt={siteName} className="h-8 w-auto object-contain" width={128} height={32} />
                            ) : (
                                <span className="text-xl font-bold tracking-tighter">{siteName}</span>
                            )}
                        </Link>
                    </div>

                    {/* Desktop Navigation (Centered) */}
                    <div className="hidden md:block flex-1">
                        <NavigationMenu className="w-full justify-center">
                            <NavigationMenuList className="gap-8">
                                {navMenu.map((item) => (
                                    <NavigationMenuItem
                                        key={item.title}
                                        onMouseEnter={() => handleMouseEnter(item.title)}
                                        onMouseLeave={handleMouseLeave}
                                        className="relative z-20"
                                    >
                                        {/* Check for children/subtitles */}
                                        {(item as any).children && (item as any).children.length > 0 ? (
                                            <>
                                                <Link
                                                    href={resolvePath(item.path)}
                                                    className={`inline-flex h-10 items-center justify-center px-4 py-2 text-xs font-medium transition-colors cursor-pointer group pointer-events-auto relative z-50 ${activeMenu === item.title ? "text-primary" : "text-muted-foreground hover:text-foreground"
                                                        }`}
                                                >
                                                    {item.title}
                                                    {item.children && item.children.length > 0 && (
                                                        <ChevronDown
                                                            className={`ml-1 h-3 w-3 transition-transform duration-200 group-hover:rotate-180 ${activeMenu === item.title ? 'rotate-180' : ''}`}
                                                        />
                                                    )}
                                                </Link>
                                                <NavigationMenuContent isOpen={activeMenu === item.title} variant="standard">
                                                    <ul className="grid gap-1 min-w-[200px] p-1">
                                                        {(item as any).children.map((child: any) => (
                                                            <li key={child.title || child.id}>
                                                                <Link
                                                                    href={resolvePath(child.path || child.url || '#')}
                                                                    className="block select-none pointer-events-auto space-y-1 rounded-md p-2.5 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                                                >
                                                                    <div className="text-sm font-medium leading-none">{child.title}</div>
                                                                </Link>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </NavigationMenuContent>
                                            </>
                                        ) : (
                                            <Link
                                                href={resolvePath(item.path)}
                                                className={`inline-flex h-10 items-center justify-center px-4 py-2 text-xs font-medium transition-colors cursor-pointer group pointer-events-auto relative z-50 ${activeMenu === item.title
                                                    ? "text-primary"
                                                    : "text-muted-foreground hover:text-foreground"
                                                    }`}
                                            >
                                                {item.title}
                                                {item.children && item.children.length > 0 && (
                                                    <ChevronDown
                                                        className={`ml-1 h-3 w-3 transition-transform duration-200 group-hover:rotate-180 ${activeMenu === item.title ? "rotate-180" : ""
                                                            }`}
                                                    />
                                                )}
                                            </Link>
                                        )}
                                    </NavigationMenuItem>
                                ))}
                            </NavigationMenuList>
                        </NavigationMenu>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center justify-end gap-1 md:gap-2">
                        {/* Desktop Search */}
                        <div className="hidden md:block w-64 mr-2">
                            {(props.showSearch ?? true) && (
                                <Suspense fallback={<div className="h-9 w-full bg-muted rounded-md animate-pulse" />}>
                                    <Search
                                        direction={navbar_direction}
                                        placeholder={props.searchPlaceholder}
                                    />
                                </Suspense>
                            )}
                        </div>

                        {/* Currency Selector */}
                        <CurrencySelector />

                        {(props.showWishlist ?? true) && (
                            <Button variant="ghost" size="icon" onClick={openWishlist} className="relative flex" aria-label="المفضلة">
                                <Heart className="h-5 w-5" />
                                {wishlistCount > 0 && <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full" />}
                            </Button>
                        )}

                        {(props.enableCustomerAccounts ?? true) && (
                            <Link href="/account">
                                <Button variant="ghost" size="icon" className="flex" aria-label="الحساب">
                                    <User className="h-5 w-5" />
                                </Button>
                            </Link>
                        )}

                        {(props.showCart ?? true) && (
                            <div className="ml-1">
                                <CartModal />
                            </div>
                        )}
                    </div>
                </div>
            </header>
        </div>
    );
}
